/**
 * The entries card: the "Add point" action and the legend of every entry.
 *
 * It is close to the legend the exported PNG carries — the same chronological
 * order and quadrant colours, and a description readable without opening
 * anything — but deliberately not identical: the export joins the year and the
 * coordinates with a ` · `, dims the description, and stops at `MAX_LEGEND_ROWS`
 * with an "and N more entries" note, while the page lists every entry at full
 * strength. Rows are sorted with `sortChronologically` (oldest first) regardless
 * of the order they are stored in, so a point placed mid-list appears where its
 * year puts it. Each row shows a quadrant-coloured swatch, the year,
 * `formatCoords` and the description in full.
 *
 * Every row is a `<button>` that opens the edit dialog. That is not decoration:
 * editing now lives behind a pointer gesture on the chart, so a keyboard or
 * screen-reader user must still be able to reach every entry. The button carries
 * no `aria-label`, so its accessible name is its visible text (year, coordinates
 * and description) and the label-in-name rule holds trivially. Because the row's
 * parts are separated only by CSS gaps — which are not text — explicit spaces
 * sit between them, so the name cannot run together into `2015(-3.5, 2)Some
 * note`.
 *
 * The rows are a real list (`ul`/`li`) with an explicit `role="list"`, because
 * `list-style: none` makes WebKit drop the implicit role and lose the announced
 * item count. This component also owns the "Add point" button, which is why it —
 * not `App` — is what moves focus to that button when the last entry is deleted.
 *
 * Both focus moves after a delete pass `preventScroll: true`. The successor row
 * can be a long way down the page, and a plain `focus()` scrolls to whatever it
 * focuses — deleting from the dialog would yank the viewport to the bottom of
 * the page, away from the chart the user was working on. The move still
 * happens; only the scroll does not.
 */

import { useEffect, useRef } from 'react'
import type { ReactElement } from 'react'

import { formatCoords, sortChronologically } from '../lib/compass'
import { PALETTE, quadrantOf } from '../lib/palette'
import type { Entry } from '../lib/types'

/** Where focus should go after an entry is deleted. */
export interface EntryFocusTarget {
  /**
   * The legend row that slid into the deleted entry's place, or `null` when the
   * list is now empty and there is nothing to focus but "Add point".
   */
  rowId: string | null
}

export interface EntryListProps {
  entries: Entry[]
  /**
   * Set after every delete. A fresh object each time, even for the same target,
   * so deleting twice in a row re-runs the focus effect.
   */
  focusAfterDelete?: EntryFocusTarget | null
  onAdd: () => void
  /** Open the edit dialog for this entry. */
  onEdit: (id: string) => void
}

export default function EntryList({
  entries,
  focusAfterDelete = null,
  onAdd,
  onEdit,
}: EntryListProps): ReactElement {
  const sorted = sortChronologically(entries)
  const listRef = useRef<HTMLUListElement>(null)
  const addButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (focusAfterDelete === null) return

    if (focusAfterDelete.rowId === null) {
      addButtonRef.current?.focus({ preventScroll: true })
      return
    }

    // Found by `data-entry-id` rather than by a selector built from the id,
    // which avoids having to escape an id that may contain punctuation.
    const rows = listRef.current?.querySelectorAll<HTMLButtonElement>(
      '[data-testid="legend-row"]',
    )
    for (const row of Array.from(rows ?? [])) {
      if (row.dataset.entryId === focusAfterDelete.rowId) {
        // The move is the point; the scroll is the bug.
        row.focus({ preventScroll: true })
        return
      }
    }
  }, [focusAfterDelete])

  return (
    <section className="card entry-list" aria-labelledby="entry-list-heading">
      <div className="entry-list__head">
        <h2 id="entry-list-heading" className="entry-list__title">
          Entries
          {sorted.length > 0 ? <span className="entry-list__count"> ({sorted.length})</span> : null}
        </h2>
        <button
          type="button"
          className="button button--primary"
          ref={addButtonRef}
          onClick={onAdd}
        >
          Add point
        </button>
      </div>

      {sorted.length === 0 ? (
        <p className="entry-list__empty" data-testid="legend-empty">
          The years you plot will be listed here in order.
        </p>
      ) : (
        <ul className="entry-list__legend" role="list" data-testid="legend" ref={listRef}>
          {sorted.map((entry) => {
            // A whitespace-only note counts as empty: rendering it would claim a
            // line (and the row gap above it) on a row with nothing to say.
            const description = entry.description ?? ''
            return (
              <li key={entry.id} className="entry-list__item">
                <button
                  type="button"
                  className="legend-row"
                  data-testid="legend-row"
                  data-entry-id={entry.id}
                  onClick={() => onEdit(entry.id)}
                >
                  <span
                    className="legend-row__swatch"
                    style={{ backgroundColor: PALETTE[quadrantOf(entry.x, entry.y)] }}
                    aria-hidden="true"
                  />
                  <span className="legend-row__meta">
                    <span className="legend-row__year">{entry.year}</span>{' '}
                    <span className="legend-row__coords">{formatCoords(entry.x, entry.y)}</span>
                  </span>
                  {description.trim() === '' ? null : (
                    <>
                      {' '}
                      <span
                        className="legend-row__description"
                        data-testid="legend-row-description"
                      >
                        {description}
                      </span>
                    </>
                  )}
                  {/* The affordance that survives without hover: this row is
                      a control, not static text. Decorative, so the accessible
                      name stays the visible text. */}
                  <span className="legend-row__chevron" aria-hidden="true">
                    ›
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
